import React, { useCallback, useEffect, useMemo, useState } from 'react';

import CookieBanner from './CookieBanner';
import CookiePreferencesModal, { type CookiePreferences } from './CookiePreferencesModal';
import { applyConsent } from './applyConsent';
import {
  getConsent,
  hasValidConsent,
  setConsent,
  type ConsentState
} from './consent';
import { OPEN_PREFERENCES_EVENT } from './events';

function toPrefs(consent: ConsentState | null): CookiePreferences {
  return {
    necessary: true,
    analytics: Boolean(consent?.analytics),
    marketing: Boolean(consent?.marketing),
    functional: Boolean(consent?.functional)
  };
}

export default function CookieConsentManager() {
  const [consent, setConsentState] = useState<ConsentState | null>(() => getConsent());
  const [bannerOpen, setBannerOpen] = useState<boolean>(() => !hasValidConsent(getConsent()));
  const [prefsOpen, setPrefsOpen] = useState(false);

  const initialPrefs = useMemo(() => toPrefs(consent), [consent]);

  const applyAndStore = useCallback(async (next: Partial<ConsentState>) => {
    const stored = setConsent(next);
    setConsentState(stored);
    setBannerOpen(false);
    await applyConsent(stored);
  }, []);

  const rejectAll = useCallback(async () => {
    await applyAndStore({ analytics: false, marketing: false, functional: false });
  }, [applyAndStore]);

  const acceptAll = useCallback(async () => {
    await applyAndStore({ analytics: true, marketing: true, functional: true });
  }, [applyAndStore]);

  const savePrefs = useCallback(
    async (next: CookiePreferences) => {
      await applyAndStore({
        analytics: Boolean(next.analytics),
        marketing: Boolean(next.marketing),
        functional: Boolean(next.functional)
      });
      setPrefsOpen(false);
    },
    [applyAndStore]
  );

  // Bootstrap: se c'è un consenso valido, applicalo subito all'avvio.
  useEffect(() => {
    const c = getConsent();
    if (!hasValidConsent(c)) return;
    // best-effort: non bloccare rendering
    if (c) applyConsent(c).catch(() => null);
  }, []);

  // Event bridge per link "Preferenze cookie"
  useEffect(() => {
    const handler = () => {
      setPrefsOpen(true);
    };
    window.addEventListener(OPEN_PREFERENCES_EVENT, handler as any);
    return () => window.removeEventListener(OPEN_PREFERENCES_EVENT, handler as any);
  }, []);

  // Se la policy cambia (version bump) o l'utente ha resettato storage, riapri banner.
  useEffect(() => {
    const c = getConsent();
    if (!hasValidConsent(c)) {
      setBannerOpen(true);
      setConsentState(c);
    }
  }, []);

  return (
    <>
      {bannerOpen ? (
        <CookieBanner
          onAcceptAll={() => {
            acceptAll().catch(() => null);
          }}
          onRejectAll={() => {
            rejectAll().catch(() => null);
          }}
          onCustomize={() => setPrefsOpen(true)}
        />
      ) : null}

      <CookiePreferencesModal
        open={prefsOpen}
        initial={initialPrefs}
        onClose={() => setPrefsOpen(false)}
        onSave={(next) => {
          savePrefs(next).catch(() => null);
        }}
        onAcceptAll={() => {
          acceptAll().catch(() => null);
        }}
        onRejectAll={() => {
          rejectAll().catch(() => null);
        }}
      />
    </>
  );
}
